import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import {
  Send, Sparkles, Plus, Loader2, Trash2, ArrowLeft, MessageSquare,
  Moon, Sun, LogOut,
} from "lucide-react";
import { toast } from "sonner";
import LanguageToggle from "@/components/LanguageToggle";
import InstallPWAButton from "@/components/InstallPWAButton";

const MODELS = [
  { key: "gemini", label: "Gemini 3.1 Pro" },
  { key: "claude", label: "Claude Sonnet 4.5" },
];

function renderMarkdownLite(text) {
  // Very light markdown: **bold**, *italic*, `code`, and preserve line breaks
  if (!text) return "";
  const esc = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  const withBold = esc.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  const withItalic = withBold.replace(/(^|\s)\*([^*\n]+)\*/g, "$1<em>$2</em>");
  const withCode = withItalic.replace(/`([^`]+)`/g, '<code class="rounded bg-muted px-1 py-0.5 text-[0.9em]">$1</code>');
  return withCode.replace(/\n/g, "<br />");
}

export default function Chat() {
  const { user, loading, logout, setUser } = useAuth();
  const { theme, toggle } = useTheme();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const location = useLocation();

  const [chats, setChats] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [title, setTitle] = useState("");
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [modelKey, setModelKey] = useState("gemini");
  const listRef = useRef(null);

  const effectiveUser = user || location.state?.user;

  useEffect(() => {
    if (!user && location.state?.user) setUser?.(location.state.user);
  }, [user, location.state, setUser]);

  useEffect(() => {
    if (loading) return;
    if (effectiveUser) return;
    navigate("/", { replace: true });
  }, [loading, effectiveUser, navigate]);

  useEffect(() => {
    if (!effectiveUser) return;
    loadChats();
  }, [effectiveUser]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, busy]);

  const loadChats = async () => {
    try {
      const { data } = await api.get("/chats");
      setChats(data);
    } catch {}
  };

  const openChat = async (id) => {
    try {
      const { data } = await api.get(`/chats/${id}`);
      setActiveId(id);
      setMessages(data.messages || []);
      setTitle(data.title || "");
    } catch {
      toast.error("Failed to open chat.");
    }
  };

  const newChat = () => {
    setActiveId(null);
    setMessages([]);
    setTitle("");
    setInput("");
  };

  const send = async () => {
    const q = input.trim();
    if (!q || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: q }]);
    setBusy(true);
    try {
      const { data } = await api.post("/chats/send", {
        message: q,
        conversation_id: activeId,
        model: modelKey,
      });
      setActiveId(data.id);
      setTitle(data.title || "");
      setMessages(data.messages || []);
      // refresh sidebar
      loadChats();
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: lang === "vi" ? "Có lỗi xảy ra. Thử lại nhé." : "Something went wrong. Please try again." }]);
    } finally {
      setBusy(false);
    }
  };

  const removeChat = async (id) => {
    try {
      await api.delete(`/chats/${id}`);
      setChats((c) => c.filter((x) => x.id !== id));
      if (activeId === id) newChat();
    } catch {}
  };

  if (loading && !effectiveUser) {
    return (
      <div className="min-h-screen grid place-items-center bg-background">
        <Loader2 className="animate-spin text-primary" />
      </div>
    );
  }
  if (!effectiveUser) return null;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Top bar */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-background/70 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="w-9 h-9 rounded-full border border-border grid place-items-center hover:bg-accent transition-colors" data-testid="back-to-dashboard-btn" title="Dashboard">
              <ArrowLeft size={15} />
            </Link>
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center text-primary-foreground">
                <Sparkles size={16} />
              </div>
              <span className="font-display text-xl tracking-tight">Readlet</span>
              <span className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground ml-1">Chat</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <InstallPWAButton />
            <LanguageToggle />
            <button
              data-testid="theme-toggle-btn"
              onClick={toggle}
              className="w-10 h-10 rounded-full border border-border grid place-items-center hover:bg-accent transition-colors"
              aria-label="Toggle theme"
            >
              {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <div className="hidden md:flex items-center gap-2 pl-3 pr-1 py-1 rounded-full border border-border">
              {effectiveUser.picture ? (
                <img src={effectiveUser.picture} alt="" className="w-7 h-7 rounded-full" />
              ) : (
                <div className="w-7 h-7 rounded-full bg-primary/20" />
              )}
              <span className="text-sm">{effectiveUser.name}</span>
              <button data-testid="logout-btn" onClick={logout} className="ml-1 w-7 h-7 rounded-full grid place-items-center hover:bg-accent" title="Sign out">
                <LogOut size={14} />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 py-6 grid lg:grid-cols-12 gap-6">
        {/* Sidebar */}
        <aside className="lg:col-span-3">
          <div className="rounded-3xl border border-border bg-card p-4 sticky top-24">
            <button
              data-testid="new-chat-btn"
              onClick={newChat}
              className="w-full h-11 rounded-xl bg-primary text-primary-foreground flex items-center justify-center gap-2 hover:brightness-110 transition mb-4"
            >
              <Plus size={16} /> {lang === "vi" ? "Cuộc trò chuyện mới" : "New chat"}
            </button>
            <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-2">
              {lang === "vi" ? "Gần đây" : "Recent"}
            </div>
            {chats.length === 0 ? (
              <p data-testid="chats-empty" className="text-sm text-muted-foreground py-4">
                {lang === "vi" ? "Chưa có cuộc trò chuyện." : "No chats yet."}
              </p>
            ) : (
              <ul className="space-y-1 max-h-[65vh] overflow-y-auto pr-1" data-testid="chats-list">
                {chats.map((c) => (
                  <li key={c.id} className="group">
                    <div className={`rounded-xl p-2.5 flex items-start gap-2 transition-colors ${activeId === c.id ? "bg-accent" : "hover:bg-accent/60"}`}>
                      <MessageSquare size={14} className="text-primary mt-1 shrink-0" />
                      <button
                        data-testid={`open-chat-${c.id}`}
                        onClick={() => openChat(c.id)}
                        className="flex-1 text-left text-sm line-clamp-2 leading-snug"
                      >
                        {c.title}
                      </button>
                      <button
                        data-testid={`delete-chat-${c.id}`}
                        onClick={() => removeChat(c.id)}
                        className="opacity-0 group-hover:opacity-100 w-7 h-7 rounded-md grid place-items-center hover:bg-destructive/10 hover:text-destructive transition"
                        title="Delete"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>

        {/* Conversation */}
        <section className="lg:col-span-9">
          <div className="rounded-3xl border border-border bg-card flex flex-col h-[calc(100vh-160px)]">
            {/* Header row */}
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <div>
                <p className="text-[10px] tracking-[0.22em] uppercase text-primary font-semibold mb-1">
                  {activeId ? (lang === "vi" ? "Cuộc trò chuyện" : "Conversation") : (lang === "vi" ? "Cuộc trò chuyện mới" : "New conversation")}
                </p>
                <h1 className="font-display text-2xl tracking-tight leading-tight line-clamp-1">
                  {title || (lang === "vi" ? "Hỏi tôi bất cứ điều gì" : "Ask me anything")}
                </h1>
              </div>
              <div className="flex items-center gap-1.5" data-testid="chat-model-picker">
                {MODELS.map((m) => (
                  <button
                    key={m.key}
                    data-testid={`chat-model-${m.key}`}
                    onClick={() => setModelKey(m.key)}
                    className={`text-xs sm:text-sm rounded-full px-3 h-8 border transition-colors ${
                      modelKey === m.key
                        ? "border-primary bg-primary/10 text-foreground"
                        : "border-border text-muted-foreground hover:text-foreground hover:border-primary/50"
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Messages */}
            <div ref={listRef} className="flex-1 overflow-y-auto px-6 py-8" data-testid="chat-messages">
              {messages.length === 0 && !busy ? (
                <div className="h-full grid place-items-center text-center px-4">
                  <div className="max-w-xl">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary grid place-items-center mx-auto mb-4">
                      <Sparkles size={22} />
                    </div>
                    <h2 className="font-display text-3xl mb-2">
                      {lang === "vi" ? "Bắt đầu trò chuyện" : "Start a conversation"}
                    </h2>
                    <p className="text-muted-foreground mb-6">
                      {lang === "vi"
                        ? "Hỏi bất cứ điều gì — giống như bạn hỏi ChatGPT hay Gemini."
                        : "Ask anything — the same way you'd ask ChatGPT or Gemini."}
                    </p>
                    <div className="grid sm:grid-cols-2 gap-2 text-left">
                      {(lang === "vi"
                        ? ["Giải thích lý thuyết tương đối cho tôi nghe","Viết email từ chối lịch sự","So sánh Python và JavaScript","Ý tưởng kinh doanh nhỏ ở Hà Nội"]
                        : ["Explain relativity to me","Draft a polite decline email","Compare Python and JavaScript","Give me small-business ideas in Boston"]
                      ).map((sample) => (
                        <button
                          key={sample}
                          data-testid={`chat-suggest-${sample.slice(0,10).replace(/\s+/g,'-')}`}
                          onClick={() => setInput(sample)}
                          className="text-sm text-left rounded-xl border border-border p-3 hover:border-primary/50 hover:bg-accent/60 transition-colors"
                        >
                          {sample}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="max-w-3xl mx-auto space-y-6">
                  {messages.map((m, i) => (
                    <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[85%] rounded-2xl px-4 py-3 leading-relaxed text-[15px] ${
                        m.role === "user"
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted text-foreground"
                      }`}>
                        <div dangerouslySetInnerHTML={{ __html: renderMarkdownLite(m.text) }} />
                        {m.model && m.role === "assistant" && (
                          <p className="mt-2 text-[10px] tracking-[0.18em] uppercase text-muted-foreground">{m.model}</p>
                        )}
                      </div>
                    </div>
                  ))}
                  {busy && (
                    <div className="flex justify-start">
                      <div className="rounded-2xl bg-muted px-4 py-3 flex items-center gap-2">
                        <Loader2 size={14} className="animate-spin" />
                        <span className="text-sm text-muted-foreground">
                          {lang === "vi" ? "Đang suy nghĩ…" : "Thinking…"}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Composer */}
            <div className="border-t border-border p-4">
              <div className="max-w-3xl mx-auto flex items-end gap-2">
                <textarea
                  data-testid="chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  rows={1}
                  placeholder={lang === "vi" ? "Nhắn cho Readlet…" : "Message Readlet…"}
                  className="flex-1 resize-none rounded-2xl border border-border bg-background px-4 py-3 text-[15px] leading-relaxed focus:outline-none focus:border-primary min-h-[48px] max-h-40"
                />
                <Button
                  data-testid="chat-send-btn"
                  onClick={send}
                  disabled={busy || !input.trim()}
                  className="rounded-full h-12 w-12 p-0 pulse-primary"
                  aria-label="Send"
                >
                  {busy ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground text-center mt-2">
                {lang === "vi"
                  ? "Readlet có thể mắc lỗi. Hãy kiểm tra thông tin quan trọng."
                  : "Readlet can make mistakes. Please verify anything important."}
              </p>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
